/*
 *  Questo component è il core del gestore degli allegati
 *  e non contiene l'accordion
 * 
 */ 

import {Component, ContentChild, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef} from '@angular/core';
import {MatCheckboxChange, MatSnackBar, MatSnackBarConfig} from '@angular/material';
import {AttachmentsBlock, AttachmentsBlocksList, GenericAttachedFile} from '@shared/components/view-files/models/attachments-interface';
import {Utilities as U} from '@shared/utilities/utilities.model';
import {UploadFileService} from '@shared/service/upload-file.service';
import {TranslateContext, TranslatePipe} from '@shared/pipes/translate.pipe';
import {FormItemsModel} from 'app/authority/authority-shared/models/form-items.model';
import {WorkingTypes} from 'app/authority/authority-shared/models/enums.model';
import {PermissionService} from '@shared/service/permission.service';
import { BookingsProfileKey } from 'app/bookings/models/enums.models';
import { PROFILES_CLAIMS_DEKRA_READONLY_KEY, PROFILE_CLAIMS_READONLY_LIST } from 'app/permissions-variables';
import { BookingFlowService } from 'app/bookings/services/booking-flow.service';
import { logger } from '@app/shared/utilities/logger.model';
import { UserPermissionsModel } from '@app/shared/models/user-permission.model';

@Component({
	selector: 'amd-attachments-viewver-core',
	templateUrl: './attachments-viewver-core.component.html',
	styleUrls: ['./attachments-viewver-core.component.scss']
})
export class AttachmentsViewerCoreComponent implements OnInit, OnChanges {
	@ContentChild('attachmentGlobalActions') attachmentGlobalActions: TemplateRef<any>;
	@ContentChild('addFile') addFile: TemplateRef<any>;
	@Input() translateContext: TranslateContext;
	@Input() attachmentList: AttachmentsBlocksList;
	@Input() canAddFile: boolean;
	@Input() canEdit: boolean;
	@Input() canDelete?: boolean;
	@Input() zipFileName: string = 'ZIP';
	@Input() actionOnZipClick: (files: GenericAttachedFile[]) => void;
	@Input() actionOnDownloadFilesClick: (files: GenericAttachedFile[]) => void;
	@Input() isReadonly?: boolean;
	@Output() uploadFileEventEmitter: EventEmitter<void> = new EventEmitter<void>();
	
	// forse sono da portare in INPUT
	isTempAttachmentsListLoaded: boolean = false; 
	accordionExpanded: boolean = false; 

	item: FormItemsModel;

	permissionMail: boolean = false;
	sharedTranslateContext: TranslateContext = TranslateContext.SHARED;
	isReadonlyProfile: boolean = false;
	isBookings: boolean = false; 
	userPermission: UserPermissionsModel;
	
	constructor(
		public snackBar: MatSnackBar,
		public bookingFlowService: BookingFlowService,
		public uploadFileService: UploadFileService,
		private translatePipe: TranslatePipe,
		private permissionService: PermissionService,
	) {

		this.userPermission = this.permissionService.getCurrentUserPermissions();
		this.isBookings = bookingFlowService.isRelief() || bookingFlowService.isPrelease();
	}
	ngOnInit(): void {
		this.isReadonlyProfile = this.isReadonly || this.userPermission.hasProfile([PROFILES_CLAIMS_DEKRA_READONLY_KEY, ...PROFILE_CLAIMS_READONLY_LIST]);
	}
	
	ngOnChanges(changes: SimpleChanges): void {
	}	

	selectAllFiles(checkboxChange: MatCheckboxChange, list: AttachmentsBlock): void {
		list.items.filter(item => item.canCheck).filter(i => i.checked = checkboxChange.checked);
	}

	/**
	 * Solo per Bookings - CO 2147 / NEMOGUI-1620
	 * controlla se il file è da inviare per email al cliente
	 * 
	 * in reltà questa funzionalità (inserita nel 2022) è inutile
	 * il filtro andava messo fuori dalla gestione allegati
	 * direttamente all'interno del dettaglio della prenotazione bookings
	 * 
	 * TODO: eliminare la funzionalità ed inserirla fuori
	 * 
	 * @param {GenericAttachedFile} file
	 * @returns {boolean}
	 */
	getPermissionMailPdf(file: GenericAttachedFile): boolean {

		if (!this.isBookings && !U.isEmpty(file)) {
			return false
		}

		return (file.blob_type == "pdf_email") ? this.userPermission.hasProfile(BOOKING_PROFILES) : false;
	}

	getAllFilesChecked(): GenericAttachedFile[] {

		if (U.isEmpty(this.attachmentList) || U.isEmpty(this.attachmentList.attachmentsBlocks)) {
			return [];
		}

		return this.attachmentList.attachmentsBlocks.map(list => list.items.filter(item => item.checked))
			.reduce((previousValue, currentValue) => [...previousValue, ...currentValue]);
	}

	downloadFiles(files: GenericAttachedFile[]): void {
		if (this.actionOnDownloadFilesClick) {
			this.actionOnDownloadFilesClick(files);
			return;
		}
		files.filter((value, index) => {
			this.uploadFileService.downloadAttachedFile(value.file_manager_id).subscribe(
				response => {
					if (!U.isVoid(response)) {
						U.downloadFile(response, value.file_manager_name);
					} else {
						const config: MatSnackBarConfig = new MatSnackBarConfig();
						config.duration = 2000;
						config.panelClass = ['snack-bar-error'];
						this.snackBar.open(this.translatePipe.transform('IMPOSSIBILE_SCARICARE_L_ALLEGATO', TranslateContext.SHARED), '', config);
					}
					if (index === files.length - 1) {
						const config: MatSnackBarConfig = new MatSnackBarConfig();
						config.duration = 2000;
						config.panelClass = ['snack-bar-success'];
						this.snackBar.open(this.translatePipe.transform('TUTTI_GLI_ALLEGATI_SONO_STATI_SCARICATI', TranslateContext.SHARED), '', config);
					}
				}, () => {
					const config: MatSnackBarConfig = new MatSnackBarConfig();
					config.duration = 2000;
					config.panelClass = ['snack-bar-error'];
					this.snackBar.open(this.translatePipe.transform('IMPOSSIBILE_SCARICARE_L_ALLEGATO', TranslateContext.SHARED), '', config);
				});
		});
	}

	downloadZip(files: GenericAttachedFile[]): void {
		if (this.actionOnZipClick) {
			this.actionOnZipClick(files);
			return;
		}
		this.uploadFileService.downloadAttachedZip(files.map(item => item.file_manager_id)).subscribe(
			response => {
				if (!U.isVoid(response)) {
					U.downloadFile(response, this.zipFileName);
				} else {
					const config: MatSnackBarConfig = new MatSnackBarConfig();
					config.duration = 2000;
					config.panelClass = ['snack-bar-error'];
					this.snackBar.open(this.translatePipe.transform('IMPOSSIBILE_SCARICARE_L_ALLEGATO', TranslateContext.SHARED), '', config);
				}
			}, () => {
				const config: MatSnackBarConfig = new MatSnackBarConfig();
				config.duration = 2000;
				config.panelClass = ['snack-bar-error'];
				this.snackBar.open(this.translatePipe.transform('IMPOSSIBILE_SCARICARE_L_ALLEGATO', TranslateContext.SHARED), '', config);
			});
	}

	uploadFilesListener(): void {
		// TODO
		// inserire controllo sul canAdd, altrimenti è incoerente...
		this.uploadFileEventEmitter.emit();
	}

	isChecked(list: AttachmentsBlock): boolean {
		return list.items.filter(item => item.checked).length === list.items.filter(item => item.canCheck).length;
	}
}

const BOOKING_PROFILES = [
	BookingsProfileKey.module_prelease_bookings_team,
	BookingsProfileKey.module_prelease_assistance_center,
	BookingsProfileKey.module_prelease_customer_service,
	BookingsProfileKey.module_prelease_customer_service_supervisor,
	BookingsProfileKey.module_prelease_sales,
	BookingsProfileKey.module_prelease_bookings_team_supervisor, 
	BookingsProfileKey.module_prelease_customer_finance,
];